/* global Vue, HOLIDAY_LIST_URL, PROPERTIES */

export default {
  name: 'OfferFilters',
  template: `
  <div class="booking-menu no-margin bg-beige-50">
    <div :class="['container', 'grid', 'select-filters', { 'container--fluid': isFluid }]">
      <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2" v-if="holidayTypes.length">
        <label for="holidayTypeFilter">{{ $t('holidayTypeFilter') }}</label>
        <select class="select-input" v-model="values.holidayType" id="holidayTypeFilter">
          <option value="">{{ $t('offers.allHolidays') }}</option>
          <option v-for="holidayType in holidayTypes" :text="holidayType.title" :value="holidayType.slug" />
        </select>
      </div>

      <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
        <label for="brandFilter">{{ $t('interestsTab.brand') }}</label>
        <select class="select-input" v-model="values.brand" id="brandFilter">
          <option value="">{{ $t('allBrands') }}</option>
          <option v-for="brand in brands" :text="brand.title" :value="brand.slug" />
        </select>
      </div>

      <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
        <label for="destinationFilter">{{ $t('destination') }}</label>
        <select class="select-input" v-model="values.destination" id="destinationFilter" >
          <option value="">{{ $t('allDestinations') }}</option>
          <option v-for="destination in destinations" :text="destination.title" :value="destination.slug" />
        </select>
      </div>

      <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
        <label for="propertyFilter">{{ $t('property') }}</label>
        <select class="select-input" v-model="values.properties" id="propertyFilter">
          <option value="">{{ $t('offers.allProperties') }}</option>
          <option v-for="property in propertyOptions" :text="property.title" :value="property.properties" />
        </select>
      </div>

      <div class="clear-filters col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2" v-if="showClearButton">
        <button @click="$emit('clear')"><i class="icon icon-close"></i> Clear filters</button>
      </div>
    </div>
  </div>
  `,
  props: {
    isFluid: {
      type: Boolean,
      default: false,
    },
    values: {
      type: Object,
    },
    properties: {
      type: Array,
    },
    availableHolidayTypes: {
      type: Array,
    },
    availableBrands: {
      type: Array,
    },
    availableDestinations: {
      type: Array,
    },
    availableProperties: {
      type: Array,
    },
  },
  emits: ['update', 'clear', 'values'],
  setup (props, { emit }) {
    const { values, availableHolidayTypes, availableBrands, availableDestinations, properties, availableProperties } = Vue.toRefs(props)

    const allHolidayTypes = Vue.ref([])
    const holidayTypes = Vue.computed(() => {
      return allHolidayTypes.value.filter(holidayType => availableHolidayTypes.value.includes(holidayType.slug))
    })

    async function getHolidayTypes () {
      const response = await fetch(HOLIDAY_LIST_URL)
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      allHolidayTypes.value = await response.json()
    }

    const allBrands = [
      {
        slug: 'collection',
        title: 'Maistra Collection',
      },
      {
        slug: 'select',
        title: 'Maistra Select',
      },
      {
        slug: 'camping',
        title: 'Maistra Camping',
      },
      {
        slug: 'city-vibes',
        title: 'Maistra City Vibes',
      },
    ]
    const brands = Vue.computed(() => {
      return allBrands.filter(brand => availableBrands.value.includes(brand.slug))
    })

    const allDestinations = [
      {
        slug: 'rovinj',
        title: 'Rovinj',
      },
      {
        slug: 'vrsar',
        title: 'Vrsar',
      },
      {
        slug: 'zagreb',
        title: 'Zagreb',
      },
      {
        slug: 'dubrovnik',
        title: 'Dubrovnik',
      },
    ]
    const destinations = Vue.computed(() => {
      return allDestinations.filter(destination => availableDestinations.value.includes(destination.slug))
    })

    const allProperties = Vue.computed(() => PROPERTIES.reduce((acc, propertyGroup) => {
      const isGroup = propertyGroup.isPropertyGroup
      const bundle = propertyGroup.bundle
      const subProperties = isGroup ? properties.value.filter((p) => p.propertyGroup === propertyGroup.slug) : []
      acc.push({
        slug: propertyGroup.slug,
        title: `${bundle ? '\xa0\xa0' : ''}${propertyGroup.title}`,
        properties: propertyGroup.isPropertyBundle
          ? properties.value.filter((p) => p.bundle === `${propertyGroup.slug}/_index`).map(p => `${p.propertyGroup}-${p.campingType}`)
          : [propertyGroup.slug, ...subProperties.map(p => `${p.propertyGroup}-${p.campingType}`)],
      })

      acc.push(
        ...subProperties
          .map((p) => ({
            properties: [`${p.propertyGroup}${p.campingType ? '-' + p.campingType : ''}`],
            slug: `${p.propertyGroup}${p.campingType ? '-' + p.campingType : ''}`,
            title: `${bundle ? '\xa0\xa0' : ''}\xa0\xa0${p.title}`,
          })),
      )

      return acc
    }, []))

    const propertyOptions = Vue.computed(() => {
      return allProperties.value.filter(property => {
        return availableProperties.value.includes(property.slug) ||
          property.properties?.some(p => availableProperties.value.includes(p))
      })
    })

    const showClearButton = Vue.computed(() => {
      return Object.values(values.value).some(value => value !== '')
    })

    Vue.onMounted(() => {
      getHolidayTypes()
    })

    Vue.watch(values, (newFilters) => {
      emit('update', newFilters)
    }, { deep: true })

    return {
      holidayTypes,
      brands,
      destinations,
      propertyOptions,
      showClearButton,
    }
  },
}
